import React, { useMemo, useState } from 'react';
import { colors } from '../../styles/tokens';
import { fmtInt, fmtMoney } from '../../utils/format';
import { metaColor, NivelMeta, rollupMeta } from '../../utils/meta';
import type { MetaFila } from '../../types/indicadores.types';
import ExportButton from '../ExportButton';
import { exportXlsx } from '../../utils/exportXlsx';
import { useEsMovil } from '../../hooks/useEsMovil';

export interface TablaMetaProps {
  filas: MetaFila[];
  /** Tiendas que el ámbito debería tener, para el par «con meta / en ámbito». */
  tiendasEsperadas: number;
}

const NIVELES: { valor: NivelMeta; etiqueta: string }[] = [
  { valor: 'regional', etiqueta: 'Regional' },
  { valor: 'supervisor', etiqueta: 'Supervisor' },
  { valor: 'tienda', etiqueta: 'Tienda' },
];

const th: React.CSSProperties = {
  textAlign: 'right',
  padding: '9px 12px',
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: '.08em',
  color: colors.texto.tertiary,
  whiteSpace: 'nowrap',
  borderBottom: `1px solid ${colors.border}`,
};

const td: React.CSSProperties = {
  textAlign: 'right',
  padding: '10px 12px',
  fontSize: 13.5,
  color: colors.texto.body,
  whiteSpace: 'nowrap',
  fontVariantNumeric: 'tabular-nums',
};

/**
 * Ajustes que sólo aplican en teléfono a los tres botones del segmentado.
 * 44 px es el mínimo tocable para un dedo; el segmentado medía 30 px de alto y
 * los tres botones se apelotonaban contra el borde derecho de la cabecera.
 */
const tocableMovil: React.CSSProperties = {
  minHeight: 44,
  padding: '10px 8px',
  fontSize: 13.5,
  width: '100%',
  textAlign: 'center',
};

/** Etiqueta de un par etiqueta/valor de la tarjeta de móvil (mismo lenguaje visual que un `<th>`). */
const etiquetaMovil: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: '.08em',
  color: colors.texto.tertiary,
};

/**
 * Un par etiqueta/valor de la tarjeta de móvil. Existe para que las cuatro
 * cifras se pinten con exactamente la misma jerarquía; `minWidth: 0` porque es
 * hijo de una rejilla y un importe largo, si no, ensancha su columna y empuja
 * la tarjeta fuera de los 390 px.
 */
const DatoMovil: React.FC<{ etiqueta: string; valor: string; destacado?: boolean }> = ({
  etiqueta,
  valor,
  destacado = false,
}) => (
  <div style={{ minWidth: 0 }}>
    <div style={etiquetaMovil}>{etiqueta}</div>
    <div
      style={{
        marginTop: 3,
        fontSize: 13.5,
        fontWeight: destacado ? 700 : 500,
        color: destacado ? colors.texto.primary : colors.texto.body,
        fontVariantNumeric: 'tabular-nums',
        overflowWrap: 'anywhere',
      }}
    >
      {valor}
    </div>
  </div>
);

/**
 * Detalle jerárquico: Regional → Supervisor → Tienda, con el mismo conjunto de
 * filas. El rollup es client-side y PURO (`rollupMeta`): cambiar de nivel no
 * dispara ninguna consulta, así que no puede dar cifras distintas del total de
 * arriba.
 *
 * ⚠️ La columna «con meta» no es adorno. Es el candado de ADUANA hecho visible:
 * un grupo con 12 de 13 tiendas está midiendo su cumplimiento contra un
 * denominador al que le falta una tienda, y sin esta columna eso es invisible.
 *
 * EN TELÉFONO NO HAY TABLA. Ocho columnas en 390 px sólo caben arrastrando la
 * pantalla de lado, y así no se compara nada. Cada grupo pasa a ser una
 * TARJETA: arriba el nombre y el cumplimiento en grande con su color —que es
 * lo que se mira—, debajo meta, venta, falta y pares. El aviso de «con meta»
 * aparece SÓLO cuando el denominador está incompleto: en escritorio la columna
 * informa, pero en el teléfono el espacio es caro y un «13 / 13» repetido en
 * cada tarjeta sólo taparía las cifras que sí se miran.
 */
const TablaMeta: React.FC<TablaMetaProps> = ({ filas, tiendasEsperadas }) => {
  const [nivel, setNivel] = useState<NivelMeta>('regional');
  const esMovil = useEsMovil();
  const grupos = useMemo(() => rollupMeta(filas, nivel), [filas, nivel]);

  const etiquetaNivel = NIVELES.find((n) => n.valor === nivel)!.etiqueta;

  // ⚠️ Este export SÍ lleva importes, al contrario que los cinco de Mi tienda.
  // Aquella regla («ningún monto en ninguna celda») venía de que la vista de
  // tienda muestra sólo pares y el Excel no debía enseñar lo que la pantalla
  // esconde. Aquí la pantalla ES el presupuesto en pesos, así que un export sin
  // importes no serviría para nada; el criterio de fondo —el Excel dice lo
  // mismo que la UI— se respeta igual.
  const hojas = useMemo(
    () => [
      {
        name: 'Presupuesto',
        header: [
          etiquetaNivel,
          'Detalle',
          'Meta $',
          'Venta $',
          'Cumplimiento %',
          'Falta $',
          'Meta pares',
          'Venta pares',
          'Cumplimiento pares %',
          'Tiendas',
          'Tiendas con meta',
        ],
        rows: grupos.map((g) => [
          g.etiqueta,
          g.detalle ?? '',
          +g.metaMonto.toFixed(2),
          +g.ventaMonto.toFixed(2),
          g.cumplimientoMonto === null ? '' : +(g.cumplimientoMonto * 100).toFixed(2),
          +g.faltanteMonto.toFixed(2),
          g.metaPares,
          g.ventaPares,
          g.cumplimientoPares === null ? '' : +(g.cumplimientoPares * 100).toFixed(2),
          g.tiendas,
          g.tiendasConMeta,
        ]),
      },
    ],
    [grupos, etiquetaNivel],
  );

  // El título y el botón de exportar se declaran una sola vez y se colocan
  // distinto en cada disposición: en escritorio el botón va pegado al
  // segmentado; en móvil sube junto al título para dejarle al segmentado el
  // ancho completo de la tarjeta.
  const titulo = (
    <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: colors.texto.primary }}>
      Detalle
    </h3>
  );

  const botonExportar = (
    <ExportButton
      onExport={() => exportXlsx(hojas, `presupuesto-${nivel}.xlsx`)}
      disabled={grupos.length === 0}
      testid="presupuesto"
    />
  );

  const segmentado = (
    <div
      role="group"
      aria-label="Nivel de agrupación"
      style={{
        // En móvil, rejilla de tres columnas iguales a todo el ancho: es el
        // control principal de la vista y en línea quedaba en 30 px de alto,
        // arrinconado contra el borde derecho.
        display: esMovil ? 'grid' : 'inline-flex',
        gridTemplateColumns: esMovil ? 'repeat(3, 1fr)' : undefined,
        width: esMovil ? '100%' : undefined,
        background: '#eef0f5',
        border: `1px solid ${colors.borderAccent}`,
        borderRadius: 999,
        padding: 3,
        gap: esMovil ? 2 : 0,
      }}
    >
      {NIVELES.map((n) => (
        <button
          key={n.valor}
          type="button"
          data-testid={`nivel-${n.valor}`}
          onClick={() => setNivel(n.valor)}
          style={{
            border: 'none',
            borderRadius: 999,
            padding: '6px 14px',
            fontFamily: 'inherit',
            fontSize: 12,
            fontWeight: nivel === n.valor ? 800 : 700,
            cursor: 'pointer',
            background: nivel === n.valor ? colors.brand : 'transparent',
            color: nivel === n.valor ? '#fff' : colors.texto.secondary,
            ...(esMovil ? tocableMovil : null),
          }}
        >
          {n.etiqueta}
        </button>
      ))}
    </div>
  );

  return (
    <div
      style={{
        background: colors.card,
        border: `1px solid ${colors.border}`,
        borderRadius: 18,
        // Márgenes laterales más estrechos en teléfono: 22 px por lado se comen
        // el 11 % del ancho útil de la pantalla.
        padding: esMovil ? '14px 12px' : '18px 22px',
        boxShadow: '0 1px 2px rgba(20,28,54,.05)',
      }}
    >
      <div
        style={{
          display: 'flex',
          // Apilado en móvil: título + exportar en una fila, y el segmentado
          // debajo ocupando el ancho.
          flexDirection: esMovil ? 'column' : 'row',
          alignItems: esMovil ? 'stretch' : 'center',
          justifyContent: 'space-between',
          gap: esMovil ? 12 : 14,
          marginBottom: 14,
          flexWrap: 'wrap',
        }}
      >
        {esMovil ? (
          <>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              {titulo}
              {/*
                `alignItems: 'stretch'` con `minHeight: 44` estira el propio
                botón hasta la altura tocable sin tocar `ExportButton`, que
                comparten otras cinco vistas del tablero.
              */}
              <div style={{ display: 'flex', alignItems: 'stretch', minHeight: 44, flex: 'none' }}>
                {botonExportar}
              </div>
            </div>
            {segmentado}
          </>
        ) : (
          <>
            {titulo}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {segmentado}
              {botonExportar}
            </div>
          </>
        )}
      </div>

      {esMovil ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {grupos.map((g) => {
            const color = metaColor(g.cumplimientoMonto);
            const incompleto = g.tiendasConMeta < g.tiendas;
            return (
              <div
                key={g.clave}
                style={{
                  background: colors.panelBg,
                  border: `1px solid ${colors.border}`,
                  borderRadius: 14,
                  padding: '12px 14px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 10,
                  }}
                >
                  {/* `minWidth: 0` o el nombre largo de una tienda se niega a
                      partirse y saca la tarjeta de la pantalla. */}
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 800,
                        color: colors.texto.primary,
                        lineHeight: 1.25,
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {g.etiqueta}
                    </div>
                    {g.detalle && (
                      <div
                        style={{
                          marginTop: 2,
                          fontSize: 11.5,
                          color: colors.texto.quaternary,
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {g.detalle}
                      </div>
                    )}
                  </div>
                  <div style={{ flex: 'none', textAlign: 'right' }}>
                    <div
                      style={{
                        fontSize: 22,
                        fontWeight: 800,
                        color,
                        lineHeight: 1.05,
                        whiteSpace: 'nowrap',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {g.cumplimientoMonto === null
                        ? '—'
                        : `${(g.cumplimientoMonto * 100).toFixed(1)} %`}
                    </div>
                    <div style={{ ...etiquetaMovil, marginTop: 3 }}>Cumplimiento</div>
                  </div>
                </div>

                <div
                  style={{
                    marginTop: 12,
                    paddingTop: 10,
                    borderTop: `1px solid ${colors.border}`,
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '10px 12px',
                  }}
                >
                  <DatoMovil etiqueta="Meta" valor={fmtMoney(g.metaMonto)} />
                  <DatoMovil etiqueta="Venta" valor={fmtMoney(g.ventaMonto)} destacado />
                  <DatoMovil
                    etiqueta="Falta"
                    valor={g.faltanteMonto > 0 ? fmtMoney(g.faltanteMonto) : '—'}
                  />
                  {/*
                    Las dos columnas de pares del escritorio caben en una sola
                    celda: son enteros cortos y así la rejilla queda en 2×2
                    exactas. La etiqueta va sin espacios alrededor de la barra
                    para que no se parta en dos líneas y desalinee su fila.
                  */}
                  <DatoMovil
                    etiqueta="Pares venta/meta"
                    valor={`${fmtInt(g.ventaPares)} / ${fmtInt(g.metaPares)}`}
                  />
                </div>

                {incompleto && (
                  <div
                    title="Hay tiendas del grupo sin meta cargada: el cumplimiento se mide contra un denominador incompleto."
                    style={{
                      marginTop: 10,
                      borderRadius: 8,
                      background: colors.estatusFueraCatalogoBg,
                      color: colors.estatusFueraCatalogoText,
                      fontSize: 12,
                      fontWeight: 700,
                      lineHeight: 1.35,
                      padding: '7px 10px',
                    }}
                  >
                    Con meta {g.tiendasConMeta} / {g.tiendas} · el cumplimiento va contra un
                    denominador incompleto
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...th, textAlign: 'left' }}>
                  {etiquetaNivel}
                </th>
                <th style={th}>Meta</th>
                <th style={th}>Venta</th>
                <th style={th}>Cumpl.</th>
                <th style={th}>Falta</th>
                <th style={th}>Pares meta</th>
                <th style={th}>Pares venta</th>
                <th style={th}>Con meta</th>
              </tr>
            </thead>
            <tbody>
              {grupos.map((g) => {
                const color = metaColor(g.cumplimientoMonto);
                const incompleto = g.tiendasConMeta < g.tiendas;
                return (
                  <tr key={g.clave} style={{ borderBottom: `1px solid ${colors.border}` }}>
                    <td style={{ ...td, textAlign: 'left', color: colors.texto.primary, fontWeight: 700 }}>
                      {g.etiqueta}
                      {g.detalle && (
                        <span style={{ color: colors.texto.quaternary, fontWeight: 500, marginLeft: 8, fontSize: 12 }}>
                          {g.detalle}
                        </span>
                      )}
                    </td>
                    <td style={td}>{fmtMoney(g.metaMonto)}</td>
                    <td style={{ ...td, color: colors.texto.primary, fontWeight: 700 }}>
                      {fmtMoney(g.ventaMonto)}
                    </td>
                    <td style={{ ...td, color, fontWeight: 800 }}>
                      {g.cumplimientoMonto === null ? '—' : `${(g.cumplimientoMonto * 100).toFixed(1)} %`}
                    </td>
                    <td style={td}>{g.faltanteMonto > 0 ? fmtMoney(g.faltanteMonto) : '—'}</td>
                    <td style={td}>{fmtInt(g.metaPares)}</td>
                    <td style={td}>{fmtInt(g.ventaPares)}</td>
                    <td
                      style={{
                        ...td,
                        color: incompleto ? colors.rojo : colors.texto.tertiary,
                        fontWeight: incompleto ? 800 : 500,
                      }}
                      title={
                        incompleto
                          ? 'Hay tiendas del grupo sin meta cargada: el cumplimiento se mide contra un denominador incompleto.'
                          : undefined
                      }
                    >
                      {g.tiendasConMeta} / {g.tiendas}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p style={{ margin: '12px 2px 0', fontSize: 12, color: colors.texto.quaternary }}>
        {grupos.length} {nivel === 'tienda' ? 'tiendas' : 'grupos'} · el ámbito completo son{' '}
        {tiendasEsperadas} tiendas. El cumplimiento de cada grupo sale de sus totales, no del
        promedio de los porcentajes de sus tiendas.
      </p>
    </div>
  );
};

export default TablaMeta;
